import API, { baseURL } from './api';
import { getSucursalInfo } from './auth';

// Ported from sge_infantil_web/src/pages/payment/pay/{index,confirm,anull}.js and
// src/pages/payment/amortization/confirm/index.js. The "Propinas" screen reuses the
// same registration list as Estudantes (services/student.js#listActiveStudents) —
// it's the exact same GET api/registration/current/registration/:sucursalId endpoint —
// and merges it client-side with per-student "extras" (extracurricular fees).

// Used by the Dashboard's "Últimas Transações" widget (LatestAppointmentsOne.jsx).
export async function findPaidPayments(sucursalId) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/payment/paid/${id}`);
}

export async function listStudentExtraconfigs(sucursalId) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/extraconfig/student/${id}`);
}

export async function payPropina(paymentId, payload) {
  return API.put(`${baseURL}api/payment/pay/${paymentId}`, payload);
}

export async function anullPayment(paymentId, payload) {
  return API.put(`${baseURL}api/payment/anull/${paymentId}`, payload);
}

export async function removeFine(paymentId, payload) {
  return API.put(`${baseURL}api/payment/remove-fine/${paymentId}`, payload);
}

// Permanently deletes an unpaid (status 0) monthly payment (propina) — both the
// SGE_PAYMENT row and its entry inside the registration's payments list. There is no
// reversal (unlike anullPayment above, which only flips a paid payment back to unpaid).
// Restricted client-side to Director/Super Admin/Gestor Administrativo — see
// ListaPropinasLayer's canRemovePropina().
export async function removePropina(paymentId) {
  return API.delete(`${baseURL}api/payment/remove/${paymentId}`);
}

export async function payExtrasMultiple(payload) {
  return API.put(`${baseURL}api/extraconfig/pay-multiple`, payload);
}

// Extras (extracurricular fees) management — available from the "Extras" row
// inside a payment's expanded detail (ListaPropinasLayer), mirroring the
// actions in sge_infantil_web/src/pages/payment/pay/index.js.
export async function removeExtra(payload) {
  return API.delete(`${baseURL}api/extraconfig/remove-extra`, { data: payload });
}

export async function revertExtraPayment(payload) {
  return API.put(`${baseURL}api/extraconfig/revert-extra-payment`, payload);
}

export async function updateExtraAmount(payload) {
  return API.put(`${baseURL}api/extraconfig/update-extra-amount`, payload);
}

// The original app posts to a trailing-slash variant of this same endpoint for the
// "ENTIDADE" (bank entity/reference) payment method — kept for parity.
export async function amortizationPay(payload, { entidade = false } = {}) {
  const path = entidade ? 'api/amortization/' : 'api/amortization';
  return API.post(`${baseURL}${path}`, payload);
}

export async function getOverduePayments(registrationId) {
  return API.get(`${baseURL}api/amortization/overdue/${registrationId}`);
}

export async function downloadExtracto(registrationId) {
  return API.get(`${baseURL}api/report/student/all/${registrationId}`);
}

export async function downloadReceipt(paymentId, extras) {
  const params = extras && extras.length ? { extras: JSON.stringify(extras) } : {};
  return API.get(`${baseURL}api/report/student/payments/${paymentId}`, { params });
}

// Dedicated "extras only" receipt (GET /api/report/student/extras-receipt/:registrationId).
// Unlike downloadReceipt above, this doesn't infer paid/pending from the underlying propina's
// status (that endpoint mirrors payment.status, which stays UNPAID here since only the extras
// were settled) — every extra passed in is rendered as paid, which is what "Pagar Somente
// Extras" needs.
export async function downloadExtrasReceipt(registrationId, { extras, paymentMethod, paymentDate }) {
  const params = { paymentMethod, paymentDate };
  if (extras && extras.length) params.extras = JSON.stringify(extras);
  return API.get(`${baseURL}api/report/student/extras-receipt/${registrationId}`, { params });
}

// Dedicated "Pagar Remanescente" receipt (GET /api/report/student/remainder-receipt/:paymentId).
// Unlike downloadReceipt, this never re-renders the full monthly itemization — only this
// transaction's figures (owed before / paid now / left after), titled "Remanescente de {Mês}".
// amountPaid is the amount charged in THIS transaction (not the payment's cumulative total,
// which is all the backend record itself keeps).
export async function downloadRemainderReceipt(paymentId, { amountPaid } = {}) {
  return API.get(`${baseURL}api/report/student/remainder-receipt/${paymentId}`, { params: { amountPaid } });
}

// Same as downloadRemainderReceipt above, but for a TAXA/emolumento that was paid
// partially (GET /api/report/student/tax-remainder-receipt/:taxPaymentId).
export async function downloadTaxRemainderReceipt(taxPaymentId, { amountPaid } = {}) {
  return API.get(`${baseURL}api/report/student/tax-remainder-receipt/${taxPaymentId}`, { params: { amountPaid } });
}

// Settles (fully or partially) the outstanding balance of a tax/emolumento that was
// already partially paid — same endpoint used for a tax's very first payment
// (PUT /api/emoluments/pay/:taxPaymentId), callable repeatedly like payPropina above.
export async function payTaxRemainder(taxPaymentId, payload) {
  return API.put(`${baseURL}api/emoluments/pay/${taxPaymentId}`, payload);
}

// Permanently removes a single tax/emolumento charge (pending or partially paid)
// from a student's registration — DELETE /api/emoluments/payments/:registrationId/:taxId.
// The backend rejects this once the charge is fully PAID. Restricted client-side to
// Director/Super Admin — see auth.js's canRemoveTaxEmolument().
export async function removeStudentTax(registrationId, taxId) {
  return API.delete(`${baseURL}api/emoluments/payments/${registrationId}/${taxId}`);
}

// Reverts a paid/partially-paid tax charge back to unpaid (status 0) without deleting
// it — the tax equivalent of anullPayment above, for a single student's tax charge
// (PUT /api/emoluments/payments/anull/:registrationId/:taxId). Restricted client-side
// to Super Admin/Admin/Director/Gestor Administrativo — see auth.js's
// canRemoveTaxEmolument().
export async function anullStudentTax(registrationId, taxId, payload) {
  return API.put(`${baseURL}api/emoluments/payments/anull/${registrationId}/${taxId}`, payload);
}

export async function downloadAmortizationReceipt(amortizationId, paymentId) {
  return API.get(`${baseURL}api/report/amortization/${amortizationId}`, { params: { paymentId } });
}

// "Facturação Agregada" (bulk/group invoicing) — ported from the (unmerged)
// navy_blue_sky branch of sge_infantil_web, src/pages/payment/bulk/index.js +
// src/services/payment.js. Two backend flows depending on selection:
//  - bulkProcessPayments: N propinas (+ optional taxes/extras + shared
//    discount) processed as one batch, independent of guardian contact.
//  - payGroup: 2+ students' propinas that share the SAME guardian contact,
//    splitting one (possibly partial) amountPaid proportionally and sending a
//    single combined comprovativo.
export async function bulkProcessPayments(payload) {
  return API.post(`${baseURL}api/payment/bulk-process-payments`, payload);
}

export async function payGroup(payload) {
  return API.post(`${baseURL}api/payment/pay-group`, payload);
}

// All taxes/emoluments configured for the sucursal (GET /api/emoluments/all/:sucursalId).
export async function listEmoluments(sucursalId) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/emoluments/all/${id}`);
}

// Combined propina+taxes comprovativo for a bulkProcessPayments transaction —
// taxIds here are the *tax payment* ids returned in
// bulkProcessPayments' response.details.taxPaymentsCreated[].taxPaymentId,
// not the original taxId.
export async function downloadBulkReceipt(registrationId, { paymentIds, taxIds, extras } = {}) {
  const params = {};
  // paymentIds/taxIds: the backend route (routes/report.js in sge_infantil_core)
  // parses these as a *plain comma-separated* string (`paymentIds.split(',')`),
  // matching how sge_infantil_web's own handlePrintAggregatedInvoice builds them
  // (`.join(',')`) — NOT a JSON array. Sending JSON.stringify(...) here used to
  // produce e.g. `["id1","id2"]`, which `.split(',')` breaks into garbled,
  // bracket/quote-wrapped chunks that never match a real id: every filtered-by-id
  // list silently came back empty, and — since the backend only clears the
  // *other* list when its own query param is entirely absent — an empty
  // paymentIds array (e.g. a tax-only aggregated invoice) skipped the filter
  // param altogether and left `payments` completely unfiltered, so the printed
  // receipt included every propina payment on the registration instead of just
  // this invoice's items. `extras` is unaffected — the backend does
  // `JSON.parse(extras)` for that one, so it must stay JSON-encoded.
  if (paymentIds?.length) params.paymentIds = paymentIds.join(",");
  if (taxIds?.length) params.taxIds = taxIds.join(",");
  if (extras?.length) params.extras = JSON.stringify(extras);
  return API.get(`${baseURL}api/report/student/payment-and-taxes-report/${registrationId}`, { params });
}

// Config CRUD for the "Taxas e Emolumentos" screen — GET /all already used
// read-only above (listEmoluments); these add create/update/deactivate.
export async function createEmolument(payload) {
  const sucursalId = getSucursalInfo()?.id || 1;
  return API.post(`${baseURL}api/emoluments`, { sucursalId, ...payload });
}

export async function updateEmolument(id, payload) {
  return API.put(`${baseURL}api/emoluments/${id}`, payload);
}

export async function setEmolumentActive(id, active) {
  return API.put(`${baseURL}api/emoluments/inactivate/${id}`, { active });
}

export async function removeEmolument(id) {
  const sucursalId = getSucursalInfo()?.id || 1;
  return API.delete(`${baseURL}api/emoluments/${id}`, { params: { sucursalId } });
}

// Config CRUD for the "Extras" screen — GET /:sucursalId already used
// read-only via listStudentExtraconfigs' sibling `/student/:sucursalId`
// variant above; this is the plain (non-student-merged) list + create/update.
// There is no delete/deactivate endpoint for extraconfig records themselves
// (only for removing an already-applied extra from a student's payment).
export async function listExtraconfigs(sucursalId) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/extraconfig/${id}`);
}

export async function createExtraconfig(payload) {
  const sucursalId = getSucursalInfo()?.id || 1;
  return API.post(`${baseURL}api/extraconfig`, { sucursalId, ...payload });
}

export async function updateExtraconfig(id, payload) {
  return API.put(`${baseURL}api/extraconfig/${id}`, payload);
}

// Assigns an existing extraconfig (extracurricular activity) to a student's
// registration — generates the same monthly payment schedule used at
// enrolment time. Available from the "Adicionar Extra" action on
// ListaEstudantesLayer.
export async function addExtraToStudent(payload) {
  return API.post(`${baseURL}api/extraconfig/add-extra`, payload);
}

// Fully unassigns an extra from a student — removes every unpaid monthly
// charge for it (payment history already settled is kept). Available from
// the "Remover Extra" action on ListaEstudantesLayer. Distinct from
// removeExtra above, which only removes a single month's unpaid charge.
export async function removeExtraFromStudent(payload) {
  return API.delete(`${baseURL}api/extraconfig/remove-extra-all`, { data: payload });
}
